import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const STATUSES = [
    { value: "", label: "All" },
    { value: "active", label: "Active" },
    { value: "paused", label: "Paused" },
    { value: "cancelled", label: "Cancelled" },
];
const PILL = {
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
    cancelled: "bg-slate-100 text-slate-500",
};
const inr = (n) => `₹${Number(n || 0).toFixed(0)}`;

const Content = () => {
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/subscriptions", { params: { status: status || undefined, page, page_size: 25 } });
            setRows(r.data.items); setTotal(r.data.total);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, page]);

    const cancel = async (id) => {
        const reason = window.prompt("Reason for force-cancel?");
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/admin/subscriptions/${id}/cancel`, { reason });
            toast.success("Cancelled");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-subscriptions-page">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                {STATUSES.map((s) => (
                    <button key={s.value} onClick={() => { setStatus(s.value); setPage(1); }}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium ${status === s.value ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{s.label}</button>
                ))}
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr><th className="px-4 py-3 text-left">Customer</th><th className="px-4 py-3 text-left">Product</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Per refill</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Next</th><th /></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? <tr><td colSpan={7} className="px-4 py-6"><div className="h-6 skeleton rounded" /></td></tr> :
                          rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No subscriptions.</td></tr> :
                          rows.map((s) => (
                            <tr key={s.id} className="hover:bg-slate-50" data-testid={`admin-sub-${s.id}`}>
                                <td className="px-4 py-3">
                                    <Link to={`/admin/users/${s.customer_id}`} className="text-slate-900 hover:text-brand-700">{s.customer_name}</Link>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <img src={s.image || "/pills-logo.jpg"} alt="" className="h-7 w-7 rounded object-cover" />
                                        <span className="text-slate-900">{s.product_name}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">{s.qty}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{inr((s.price_at_creation || 0) * s.qty)}</td>
                                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${PILL[s.status]}`}>{s.status}</span></td>
                                <td className="px-4 py-3 text-xs text-slate-700">{s.next_refill_date || "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    {s.status !== "cancelled" && (
                                        <button onClick={() => cancel(s.id)} className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50" data-testid={`admin-sub-cancel-${s.id}`}>Force cancel</button>
                                    )}
                                </td>
                            </tr>
                          ))}
                    </tbody>
                </table>
                {!loading && total > 25 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>Page {page} of {Math.ceil(total / 25)}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdminSubscriptions = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Subscriptions" subtitle="Oversight of all auto-refill subscriptions">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminSubscriptions;
